import type { EvaluationMetadata } from "@/api/pizza-checker/schemas";

/**
 * Obstacle layer (src/components/evaluation/obstacle-overlay.tsx): the `obstacle_in_pizza` photo-quality check
 * of pizza-backend (vision.photo_quality_checks.build_obstacle_check). Its details are an open dictionary in
 * the API schema, so the shape is typed here. All geometry is in source-image pixels.
 */

/** the classes the backend's obstacle model (v8) reports; another name is shown as it comes */
export const OBSTACLE_CLASS_NAMES = ["hand", "paper", "lid", "cup"] as const;
export type ObstacleClassName = (typeof OBSTACLE_CLASS_NAMES)[number];

export const isKnownObstacleClass = (name: string): name is ObstacleClassName =>
  (OBSTACLE_CLASS_NAMES as readonly string[]).includes(name);

export interface ObstacleClass {
  name: ObstacleClassName | string;
  /** highest confidence of the class, 0..1 */
  score: number;
  /** share of the pizza this class covers, 0..1 */
  coverage: number;
  area_px: number;
  /** where a label sits best: the innermost point of the part over the pizza */
  anchor: [number, number] | null;
  /** rings of [x, y] points: outer boundaries and holes alike, filled with the even-odd rule */
  polygons: [number, number][][];
}

export interface ObstacleSegmentation {
  name: string;
  version: string | null;
  /** share of the pizza covered by all obstacles together, 0..1; null when the model did not run */
  coverage: number | null;
  threshold: number;
  /** false = shadow mode: recorded, but the crust gap alone decides */
  enforced: boolean;
  would_fail: boolean;
  /** why the model did not run (model_unavailable, no_image, error: ...) */
  skipped: string | null;
  classes: ObstacleClass[];
  roi: [number, number, number, number] | null;
  inference_ms: number | null;
}

export interface ObstacleDetails {
  /** largest gap in the crust ring as a share of the circumference */
  max_gap_ratio?: number;
  threshold_ratio?: number;
  /** the largest gap as an arc: start in deg clockwise from 3 o'clock, length in deg */
  max_gap?: { start_deg: number; size_deg: number } | null;
  circle?: { x: number; y: number; r: number } | null;
  /** which signal failed: "segmentation" (object over the pizza) and / or "crust_gap" (missing crust) */
  detected_by?: string[];
  segmentation?: ObstacleSegmentation;
}

export interface ObstacleCheck {
  status: string;
  reason?: string | null;
  details?: ObstacleDetails;
}

export interface ObstacleFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** mask = outlines and hatching over the photo; source = the bare photo with a thin dashed outline */
export type ObstacleViewMode = "mask" | "source";

/** One hue per class, picked to stay readable over a pizza (reds, yellows and browns). */
export const OBSTACLE_COLORS: Record<string, string> = {
  hand: "#f43f5e",
  paper: "#38bdf8",
  lid: "#a78bfa",
  cup: "#2dd4bf",
};
export const OBSTACLE_FALLBACK_COLOR = "#f43f5e";
export const OBSTACLE_GAP_COLOR = "#f59e0b";
/** an object the model found beside the pizza: shown, but it does not count */
export const OBSTACLE_MUTED_COLOR = "#94a3b8";

export const obstacleColor = (name: string) => OBSTACLE_COLORS[name] ?? OBSTACLE_FALLBACK_COLOR;

/** the stored check of an evaluation (undefined on evaluations without photo-quality checks) */
export function storedObstacleCheck(
  metadata: Pick<EvaluationMetadata, "photo_quality_evaluation"> | null | undefined,
): ObstacleCheck | undefined {
  return metadata?.photo_quality_evaluation?.checks?.obstacle_in_pizza as ObstacleCheck | undefined;
}

/** true when the check carries what the layer draws (evaluations made before the segmentation do not) */
export function hasObstacleGeometry(check: ObstacleCheck | null | undefined): boolean {
  return !!check?.details?.segmentation && !!check.details.circle;
}

export const isObstacleNok = (check: ObstacleCheck | null | undefined) => check?.status === "NOK";

/** SVG path of a class mask (use fill-rule evenodd: holes are plain rings) */
export function obstaclePath(polygons: [number, number][][]): string {
  return polygons
    .filter((ring) => ring.length >= 3)
    .map((ring) => `M ${ring.map(([x, y]) => `${x} ${y}`).join(" L ")} Z`)
    .join(" ");
}

/** classes sorted by what they cover, the biggest first */
export function sortedObstacleClasses(segmentation: ObstacleSegmentation | undefined): ObstacleClass[] {
  return [...(segmentation?.classes ?? [])].sort((a, b) => b.coverage - a.coverage || b.area_px - a.area_px);
}

/**
 * The crop the segmentation looked at, padded like the evaluated pizza's ROI: the frame of the obstacle layer.
 * It spans the whole pizza circle, while the evaluation's pizza_roi is only the visible crust's bounding box
 * (and "Invalid Photo" evaluations have none).
 */
export function obstacleRoiFrame(check: ObstacleCheck | null | undefined, padding = 0.02): ObstacleFrame | null {
  const roi = check?.details?.segmentation?.roi;
  if (!roi || roi[2] <= 0 || roi[3] <= 0) return null;
  return {
    x: roi[0] - roi[2] * padding,
    y: roi[1] - roi[3] * padding,
    width: roi[2] * (1 + 2 * padding),
    height: roi[3] * (1 + 2 * padding),
  };
}
